import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Button, Dialog, DialogContent, Grid, useTheme } from "@mui/material";
import { isEqual } from "lodash";
import { useTranslation } from "react-i18next";
import { v4 as uuidv4 } from "uuid";
import DialogTitle from "../../../../../../../../containers/DialogTitle/DialogTitle";
import { TravelExpenseRef } from "../../../../../../../../models/travel-expenses";
import { Props, State } from "./interfaces";
import { MainContainer, FieldsContainer } from "./styles";
import DialogActions from "../../../../../../../../containers/DialogActions/DialogActions";
import ConfirmationChangesDialog from "../../../../../../../../containers/ConfirmationChangesDialog/ConfirmationChangesDialog";
import { DocumentDTO } from "../../../../../../../../models/documents";
import ExchangeRateSuggestions from "./ExchangeRateSuggestions/ExchangeRateSuggestions";
import CustomConfirmationChangesDialog from "../../../../../../../../containers/CustomConfirmationChangesDialog/CustomConfirmationChangesDialog";
import { useAppSelector } from "../../../../../../../../custom-hooks/redux/redux";
import InfoAndNotesSection from "./InfoAndNotesSection/InfoAndNotesSection";
import { ExpenseReportView } from "../../../../../utils";
import ReceiptReadOnlyView from "./ReceiptReadOnlyView/ReceiptReadOnlyView";
import AddReceiptFields from "./AddReceiptFields/AddReceiptFields";
import { TravelExpenseAuditNotesDTO } from "../../../../../../../../models/travel-expense-audit-notes";
import TravelExpenseHints from "./TravelExpenseHints/TravelExpenseHints";
import { TravelExpenseDTO } from "src/models/travel-expenses/travel-expense-dto";
import { TravelExpenseTypeRef } from "src/models/travel-expense-types";
const AddEditTravelExpense = ({
  effect = "add",
  item,
  handleSaveTravelExpense,
  children,
  employee,
  minDate,
  maxDate,
  view,
  readOnly = false,
  disabledAuditActions = false,
  handleUpdateAuditActions,
  hideAuditNotes = false,
  expenseReportStatusId,
  isManagedByLink = false,
}: Props) => {
  const { t } = useTranslation();
  const tRP = "components.add_edit_travel_expense";

  const theme = useTheme();

  const { currentCompanyAccount } = useAppSelector((state) => state.currentUserReducer);

  const DEFAULT_ITEM: Partial<TravelExpenseDTO> = useMemo(() => {
    return {
      legalEntity: item?.legalEntity ?? employee?.legalEntity ?? null,
      companyAccount: item?.companyAccount ?? employee?.companyAccount ?? currentCompanyAccount ?? null,
      date: item?.date ?? new Date(),
      type: item?.type ?? null,
      expenseReport: item?.expenseReport ?? null,
      amount: item?.amount ?? 0,
      currency: item?.currency ?? employee?.legalEntity?.defaultCurrency ?? null,
      exchangeRate: item?.exchangeRate ?? 1,
      convertedAmount: item?.convertedAmount ?? 0,
      convertedAmountCurrency: item?.convertedAmountCurrency ?? employee?.legalEntity?.defaultCurrency ?? null,
      description: item?.description ?? "",
      receiptsDocument: item?.receiptsDocument ?? null,
      clientName: item?.clientName ?? "",
      guestNames: item?.guestNames ?? "",
      vatAmount: item?.vatAmount ?? 0,
      vatRate: item?.vatRate ?? 0,
      acceptedAmount: item?.acceptedAmount ?? 0,
      // auditDocument: item?.auditDocument ?? null,
      auditAccepted: item?.auditAccepted ?? null,
      auditNotes: item?.auditNotes ?? [],
      difference: item?.difference ?? 0,
      costIncurredComment: item?.costIncurredComment ?? "",
      amountAndTypeJustifiedComment: item?.amountAndTypeJustifiedComment ?? "",
      compliantReceiptComment: item?.compliantReceiptComment ?? "",
      costIncurred: item?.costIncurred ?? false,
      amountAndTypeJustified: item?.amountAndTypeJustified ?? false,
      compliantReceipt: item?.compliantReceipt ?? false,
      country: item?.country ?? null,
      location: item?.location ?? "",
    };
  }, [employee, item, currentCompanyAccount]);

  const INITIAL_STATE: State = useMemo(() => {
    return {
      isDialogOpen: false,
      loading: false,
      travelExpense: item ?? (DEFAULT_ITEM as TravelExpenseDTO),
      isCCDOpen: false,
      isCCCDOpen: false,
      areFieldsValidated: effect === "edit",
      auditActionsMutated: false,
    };
  }, [effect, DEFAULT_ITEM, item]);

  const [state, setState] = useState<State>({
    ...INITIAL_STATE,
  });

  const isFirstRender = useRef<boolean>(true);

  const handleChange = useCallback((field: keyof TravelExpenseRef, value: TravelExpenseRef[keyof TravelExpenseRef]) => {
    setState((state) => {
      if (field === "type") {
        const newType = value as TravelExpenseTypeRef;
        return {
          ...state,
          travelExpense: {
            ...state.travelExpense,
            type: newType,
            description: newType.includeDescription ? state.travelExpense.description : DEFAULT_ITEM.description ?? "",
            clientName: newType.includeClientName ? state.travelExpense.clientName : DEFAULT_ITEM.clientName ?? "",
            guestNames: newType.includeGuestNames ? state.travelExpense.guestNames : DEFAULT_ITEM.guestNames ?? "",
            vatAmount: newType.includeVatAmount ? state.travelExpense.vatAmount : DEFAULT_ITEM.vatAmount ?? 0,
            vatRate: newType.includeVatRate ? state.travelExpense.vatRate : DEFAULT_ITEM.vatRate ?? 0,
            location: newType.includeLocation ? state.travelExpense.location : DEFAULT_ITEM.location ?? "",
          },
        };
      }

      return {
        ...state,
        travelExpense: {
          ...state.travelExpense,
          [field]: value,
          ...(field === "amount" && {
            acceptedAmount: Number(value),
            difference: 0,
            vatAmount: Number(state.travelExpense.vatRate) * Number(value),
          }),
          ...(field === "vatRate" && {
            vatAmount: Number(value) * Number(state.travelExpense.amount),
          }),
          ...(field === "vatAmount" && {
            vatRate: Number(state.travelExpense.amount) / Number(value),
          }),
        },
      };
    });
  }, []);

  const handleNotifyOnDocumentSave = useCallback(
    (document: DocumentDTO) => {
      setState((state) => ({
        ...state,
        travelExpense: {
          ...state.travelExpense,
          receiptsDocument: { ...document, employee: employee },
        },
      }));
    },
    [employee]
  );

  const handleSave = useCallback(async () => {
    handleSaveTravelExpense(
      {
        ...state.travelExpense,
        vatRate: state.travelExpense.vatRate ?? 0,
        ...(effect === "add" && { id: uuidv4() }),
      } as TravelExpenseDTO,
      state.auditActionsMutated
    );

    setState({ ...INITIAL_STATE });

    isFirstRender.current = true;
  }, [INITIAL_STATE, effect, handleSaveTravelExpense, state.travelExpense, employee, state.auditActionsMutated]);

  const handleDiscardChanges = useCallback(() => {
    // const auditDoc = state.travelExpense.auditDocument;
    const actualAuditNotes = state.travelExpense.auditNotes;

    state.auditActionsMutated &&
      handleUpdateAuditActions &&
      handleUpdateAuditActions(state.travelExpense.id, actualAuditNotes);

    setState({ ...INITIAL_STATE });

    isFirstRender.current = true;
  }, [
    INITIAL_STATE,
    state.auditActionsMutated,
    // state.travelExpense.auditDocument,
    state.travelExpense.id,
    handleUpdateAuditActions,
  ]);

  const resetAndCloseDialog = (): void => {
    const travelExpenseToCompare = {
      ...state.travelExpense,
      // auditDocument: INITIAL_STATE.travelExpense.auditDocument,
      auditNotes: INITIAL_STATE.travelExpense.auditNotes,
    };

    if (!isEqual(travelExpenseToCompare, INITIAL_STATE.travelExpense)) {
      setState((state) => ({
        ...state,
        isCCDOpen: true,
      }));
    } else {
      handleDiscardChanges();
    }
  };

  const areFieldsValidated = useCallback((): boolean => {
    return (
      !!state.travelExpense.legalEntity &&
      !!state.travelExpense.companyAccount &&
      !!state.travelExpense.date &&
      !!state.travelExpense.type &&
      !!state.travelExpense.currency &&
      !!state.travelExpense.country &&
      !!state.travelExpense.description &&
      state.travelExpense.amount > 0
    );
  }, [state.travelExpense]);

  useEffect(() => {
    setState((state) => ({
      ...state,
      areFieldsValidated: areFieldsValidated(),
    }));
  }, [state.travelExpense, areFieldsValidated]);

  useEffect(() => {
    if (
      isEqual(state.travelExpense.legalEntity?.defaultCurrency?.id, state.travelExpense.currency?.id) ||
      state.travelExpense.exchangeRate === 0
    )
      setState((state) => ({
        ...state,
        travelExpense: {
          ...state.travelExpense,
          exchangeRate: 1,
        },
      }));
  }, [state.travelExpense.exchangeRate, state.travelExpense.legalEntity, state.travelExpense.currency]);

  useEffect(() => {
    setState((state) => ({
      ...state,
      travelExpense: {
        ...state.travelExpense,
        convertedAmount: state.travelExpense.amount * state.travelExpense.exchangeRate,
      },
    }));
  }, [state.travelExpense.amount, state.travelExpense.exchangeRate]);

  useEffect(() => {
    !!item &&
      setState((state) => ({
        ...state,
        travelExpense: {
          ...item,
          companyAccount: item?.companyAccount ?? employee.legalEntity?.companyAccount ?? currentCompanyAccount ?? null,
          legalEntity: item?.legalEntity ?? employee.legalEntity ?? null,
        },
      }));

    return () => {
      if (effect === "add") setState({ ...INITIAL_STATE });
    };
  }, [item, effect, INITIAL_STATE, currentCompanyAccount]);

  const handleCloseCCCCD = useCallback(() => {
    setState((state) => ({
      ...state,
      isCCCDOpen: false,
    }));
  }, []);

  const handleDeleteDocument = useCallback(async () => {
    setState((state) => ({
      ...state,
      travelExpense: {
        ...state.travelExpense,
        receiptsDocument: null,
      },
      isCCCDOpen: false,
    }));
  }, []);

  const updateAuditDoc = useCallback((document: DocumentDTO | null) => {
    setState((state) => ({
      ...state,
      travelExpense: {
        ...state.travelExpense,
        auditDocument: document,
      },
      auditActionsMutated: true,
    }));
  }, []);

  const updateLastAuditNote = useCallback((auditNote: TravelExpenseAuditNotesDTO) => {
    !!auditNote &&
      setState((state) => ({
        ...state,
        travelExpense: {
          ...state.travelExpense,
          auditNotes: [auditNote],
        },
        auditActionsMutated: true,
      }));
  }, []);

  return (
    <>
      <>
        {!!children && effect === "edit" && (
          <Grid
            onClick={() => {
              setState((state) => ({ ...state, isDialogOpen: true }));
            }}
            sx={{
              "&:hover": {
                cursor: "pointer",
              },
            }}
          >
            {children}
          </Grid>
        )}

        {!children && effect === "add" && (
          <Button
            onClick={() => {
              setState((state) => ({ ...state, isDialogOpen: true }));
            }}
            variant="contained"
            sx={{ textTransform: "none" }}
          >
            {`${t(`${tRP}.add_receipt`)}`}
          </Button>
        )}
      </>

      <Dialog
        open={state.isDialogOpen}
        onClose={resetAndCloseDialog}
        fullWidth
        maxWidth={effect === "add" ? "xl" : effect === "edit" && readOnly ? "sm" : "lg"}
      >
        <DialogTitle
          title={effect === "add" ? t(`${tRP}.title_add`) : t(`${tRP}.title_edit`)}
          handleClose={resetAndCloseDialog}
        />
        <DialogContent>
          <MainContainer
            container
            isEditMode={effect === "edit"}
          >
            {view === ExpenseReportView.employee && !readOnly && (
              <FieldsContainer
                flex={1}
                isEditMode={effect === "edit"}
              >
                <AddReceiptFields
                  travelExpense={state.travelExpense}
                  handleChange={handleChange}
                  readOnly={readOnly}
                  employee={employee}
                  minDate={minDate}
                  maxDate={maxDate}
                  handleNotifyOnDocumentSave={handleNotifyOnDocumentSave}
                  openCCCDDialog={() => {
                    setState((state) => ({
                      ...state,
                      isCCCDOpen: true,
                    }));
                  }}
                  exchangeRateSuggestions={
                    <>
                      {state.isDialogOpen && (
                        <ExchangeRateSuggestions
                          date={state.travelExpense.date}
                          sourceCurrency={state.travelExpense?.currency}
                          destinationCurrency={state.travelExpense?.legalEntity?.defaultCurrency ?? null}
                          legalEntity={state.travelExpense?.legalEntity}
                          handleNotify={(exchangeRate: number) => {
                            if (effect === "edit" && isFirstRender.current) {
                              isFirstRender.current = false;
                            } else {
                              handleChange("exchangeRate", exchangeRate);

                              isFirstRender.current = false;
                            }
                          }}
                        />
                      )}
                    </>
                  }
                />
              </FieldsContainer>
            )}

            {state.isDialogOpen && (
              <>
                {!expenseReportStatusId ? (
                  <>
                    <TravelExpenseHints receiptDocument={state.travelExpense.receiptsDocument} />
                  </>
                ) : (
                  <Grid
                    flex={1}
                    sx={{ height: "100%", overflow: "auto", background: theme.palette.background.paper }}
                  >
                    {view === ExpenseReportView.employee && !readOnly && (
                      <InfoAndNotesSection
                        travelExpense={state.travelExpense}
                        employee={employee}
                        handleNotifyParent={updateAuditDoc}
                        defaultTravelExpense={INITIAL_STATE.travelExpense}
                        disabledAuditActions={disabledAuditActions}
                        updateLastAuditNote={updateLastAuditNote}
                        hideAuditNotes={hideAuditNotes}
                        isManagedByLink={isManagedByLink}
                      />
                    )}

                    {readOnly && (
                      <ReceiptReadOnlyView
                        travelExpense={state.travelExpense}
                        employee={employee}
                        handleNotifyParent={updateAuditDoc}
                        defaultTravelExpense={INITIAL_STATE.travelExpense}
                        disabledAuditActions={disabledAuditActions}
                        updateLastAuditNote={updateLastAuditNote}
                        hideAuditNotes={hideAuditNotes}
                        isManagedByLink={isManagedByLink}
                      />
                    )}
                  </Grid>
                )}
              </>
            )}
          </MainContainer>
        </DialogContent>
        {!readOnly && (
          <DialogActions
            handleSave={handleSave}
            disabledSaveButton={!areFieldsValidated() || readOnly}
            handleCancel={resetAndCloseDialog}
            customSaveName={`${t(`buttons.confirm`)}`}
          />
        )}
      </Dialog>

      <ConfirmationChangesDialog
        open={state.isCCDOpen}
        handleCancel={() => setState({ ...state, isCCDOpen: false })}
        handleDiscard={handleDiscardChanges}
        alertAddTextKey={`${tRP}.alertCancelReceipt`}
      />

      <CustomConfirmationChangesDialog
        open={state.isCCCDOpen}
        loading={state.loading}
        title={"Unlink document"}
        handleCancel={handleCloseCCCCD}
        actionsAndHandlers={[
          {
            action: "yes",
            buttonVariant: "contained",
            handler: handleDeleteDocument,
          },
        ]}
      >
        {t(`${tRP}.unlinkAlert`)}
      </CustomConfirmationChangesDialog>
    </>
  );
};

export default AddEditTravelExpense;
